import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Drawer, Spin } from "antd";
import {
  AppstoreOutlined,
  LeftOutlined,
  MenuOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  WORKSPACE_ROOT_KEYS,
  buildNavigationTree,
  buildRailGroups,
  findActiveLineage,
  firstNavigableLeaf,
  navigationItemLabel,
  normalizeNavKey,
  shouldOpenWorkspaceDefault,
  workspaceItemPath,
} from "./navigationTree";
import "./module-workspace.css";
import MySpaceRequestLauncher from "./MySpaceRequestLauncher";
import { checkPermissions } from "@comps/commonFunctions";

const WORKSPACE_COPY = {
  mySpace: "Your profile, time, requests, pay and documents in one place.",
  people: "Employees, hiring, development and the complete employee lifecycle.",
  time: "Timesheets, shifts, rosters, leave and requests — together.",
  payroll: "Payroll, salary, expenses, loans and travel administration.",
  insights: "Role-based reports and operational insights in one workspace.",
  admin: "Organization, workflows, access and system configuration.",
};

/**
 * Lets a screen put its own tabs in the workspace tab strip instead of spending a
 * second row on them. While a page supplies tabs they replace the navigation tabs
 * for that strip, so the strip never shows the page's own name next to them.
 */
export const WorkspaceTabsContext = React.createContext({
  setPageTabs: () => {},
  stripAvailable: false,
});

export const useWorkspacePageTabs = (tabsNode, deps = []) => {
  const { setPageTabs, stripAvailable } = useContext(WorkspaceTabsContext);

  useEffect(() => {
    if (!stripAvailable) return undefined;

    setPageTabs(tabsNode);
    return () => setPageTabs(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripAvailable, setPageTabs, ...deps]);

  return stripAvailable;
};

const ModuleWorkspace = ({
  navigation,
  notificationModules = [],
  loading = false,
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const tabRefs = useRef([]);
  const tabsContainerRef = useRef(null);
  const tabSentinelRef = useRef(null);
  const contentRef = useRef(null);
  const previousPathRef = useRef(location.pathname);
  const previousWorkspaceRef = useRef(null);
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const [workspaceRailCollapsed, setWorkspaceRailCollapsed] = useState(false);
  const [tabsPinned, setTabsPinned] = useState(false);
  const [pageTabs, setPageTabs] = useState(null);
  const [tabOverflow, setTabOverflow] = useState({ left: false, right: false });
  const moduleKey = localStorage.getItem("modulekey") || "";
  const isStandaloneProfile = /^\/(?:my-profile|user\/profile|employees\/profile)(?:\/|$)/.test(location.pathname);
  const canViewReports = checkPermissions("reportsAll");

  const tree = useMemo(
    () => buildNavigationTree(
      navigation?.permissions || [],
      navigation?.custom_pay_items || [],
      { includeScopedReports: !!canViewReports }
    ),
    [navigation, canViewReports]
  );

  const lineage = useMemo(
    () => findActiveLineage(tree, location.pathname, moduleKey),
    [tree, location.pathname, moduleKey]
  );

  const workspace = lineage?.[0];
  const workspaceKey = workspace?.module_key;
  const isWorkspace = !isStandaloneProfile && WORKSPACE_ROOT_KEYS.includes(workspaceKey);
  const tabs = useMemo(
    () => (isWorkspace ? workspace.children || [] : []),
    [isWorkspace, workspace]
  );
  const activeTab = lineage?.[1] || tabs[0];
  const railGroups = useMemo(() => buildRailGroups(activeTab), [activeTab]);
  const activeItem = lineage?.[lineage.length - 1];

  useEffect(() => {
    if (!isWorkspace || !workspace || !activeTab) return;

    const needsDefault = shouldOpenWorkspaceDefault(lineage, activeItem);

    if (needsDefault) {
      const destination = firstNavigableLeaf(activeTab);
      const destinationPath = workspaceItemPath(workspaceKey, destination);
      if (destinationPath && destinationPath !== location.pathname) {
        navigate(destinationPath, { replace: true });
      }
    }
  }, [
    activeItem,
    activeTab,
    isWorkspace,
    location.pathname,
    navigate,
    lineage,
    workspace,
    workspaceKey,
  ]);

  const syncTabOverflow = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    setTabOverflow({
      left: container.scrollLeft > 1,
      right: maxScroll > 1 && container.scrollLeft < maxScroll - 1,
    });
  }, []);

  useEffect(() => {
    if (!isWorkspace) {
      setTabOverflow({ left: false, right: false });
      return undefined;
    }

    const container = tabsContainerRef.current;
    if (!container) return undefined;

    const frameId = window.requestAnimationFrame(syncTabOverflow);
    container.addEventListener("scroll", syncTabOverflow, { passive: true });

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", syncTabOverflow);
      return () => {
        window.cancelAnimationFrame(frameId);
        container.removeEventListener("scroll", syncTabOverflow);
        window.removeEventListener("resize", syncTabOverflow);
      };
    }

    const observer = new ResizeObserver(syncTabOverflow);
    observer.observe(container);

    return () => {
      window.cancelAnimationFrame(frameId);
      container.removeEventListener("scroll", syncTabOverflow);
      observer.disconnect();
    };
  }, [isWorkspace, pageTabs, syncTabOverflow, tabs.length]);

  useEffect(() => {
    if (!isWorkspace) return;

    const frameId = window.requestAnimationFrame(() => {
      const activeIndex = tabs.findIndex((tab) => tab.id === activeTab?.id);
      const activeTabNode = activeIndex >= 0
        ? tabRefs.current[activeIndex]
        : tabsContainerRef.current?.querySelector('[role="tab"][aria-selected="true"]');

      scrollTabHorizontally(activeTabNode);
      syncTabOverflow();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [activeTab?.id, isWorkspace, pageTabs, syncTabOverflow, tabs]);

  useEffect(() => {
    if (previousPathRef.current !== location.pathname && isWorkspace) {
      previousPathRef.current = location.pathname;
      window.requestAnimationFrame(() => contentRef.current?.focus({ preventScroll: true }));
    }
  }, [isWorkspace, location.pathname]);

  useEffect(() => {
    if (!isWorkspace || !workspaceKey) return;

    if (previousWorkspaceRef.current && previousWorkspaceRef.current !== workspaceKey) {
      contentRef.current?.closest(".maincontent")?.scrollTo({ top: 0, behavior: "auto" });
    }
    previousWorkspaceRef.current = workspaceKey;
  }, [isWorkspace, workspaceKey]);

  useEffect(() => {
    const sentinel = tabSentinelRef.current;
    const scrollRoot = sentinel?.closest(".maincontent");
    if (!isWorkspace || !sentinel || !scrollRoot) {
      setTabsPinned(false);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setTabsPinned(!entry.isIntersecting),
      { root: scrollRoot, threshold: 0 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [isWorkspace, workspaceKey]);

  const workspaceTabsValue = useMemo(
    () => ({ setPageTabs, stripAvailable: isWorkspace }),
    [isWorkspace]
  );

  if (!isWorkspace) {
    return (
      <WorkspaceTabsContext.Provider value={ workspaceTabsValue }>
        { children }
      </WorkspaceTabsContext.Provider>
    );
  }

  const openItem = (item, replace = false) => {
    const destination = firstNavigableLeaf(item);
    const path = workspaceItemPath(workspaceKey, destination);
    if (!path) return;

    localStorage.setItem("modulekey", destination.module_key || "");
    navigate(path, { replace });
    setMobileRailOpen(false);
  };

  const onTabKeyDown = (event, index) => {
    let nextIndex = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;

    if (nextIndex !== null) {
      event.preventDefault();
      tabRefs.current[nextIndex]?.focus();
    }
  };

  const activeLabel = navigationItemLabel(activeItem || activeTab);

  return (
    <WorkspaceTabsContext.Provider value={ workspaceTabsValue }>
    <section className="module-workspace">
      {/* Hidden to give each page more vertical content space above the toolbar.
      <header className="module-workspace__header">
        <div>
          <h1>{ navigationItemLabel(workspace) }</h1>
          <p>{ WORKSPACE_COPY[workspaceKey] || "Your role-based workspace." }</p>
        </div>
        { normalizeNavKey(workspaceKey) === "myspace" ? <MySpaceRequestLauncher modules={ workspace.children } /> : null }
      </header>
      */}

      { normalizeNavKey(workspaceKey) === "myspace" ? (
        <div className="module-workspace__actions">
          <h1>My Space</h1>
          <MySpaceRequestLauncher modules={ workspace.children } />
        </div>
      ) : null }

      <div ref={ tabSentinelRef } className="module-workspace__tabs-sentinel" aria-hidden="true" />

      <div className={ `module-workspace__tabs-shell${tabsPinned ? " module-workspace__tabs-shell--pinned" : ""}${tabOverflow.left ? " module-workspace__tabs-shell--overflow-left" : ""}${tabOverflow.right ? " module-workspace__tabs-shell--overflow-right" : ""}` }>
        <div
          ref={ tabsContainerRef }
          className="module-workspace__tabs"
          role="tablist"
          aria-label={ `${navigationItemLabel(workspace)} sections` }
        >
          { pageTabs || tabs.map((tab, index) => {
            const selected = tab.id === activeTab?.id;
            const count = getNotificationCount(notificationModules, tab);

            return (
              <button
                key={ tab.id || tab.module_key }
                ref={ (element) => { tabRefs.current[index] = element; } }
                type="button"
                role="tab"
                aria-selected={ selected }
                tabIndex={ selected ? 0 : -1 }
                className={ `module-workspace__tab${selected ? " module-workspace__tab--active" : ""}` }
                onClick={ () => openItem(tab) }
                onKeyDown={ (event) => onTabKeyDown(event, index) }
              >
                <span>{ navigationItemLabel(tab) }</span>
                { count > 0 ? <Badge count={ count } overflowCount={ 99 } /> : null }
              </button>
            );
          }) }
        </div>
      </div>

      <div className={ `module-workspace__frame${railGroups.length ? "" : " module-workspace__frame--full"}${workspaceRailCollapsed ? " module-workspace__frame--rail-collapsed" : ""}` }>
        { railGroups.length ? (
          <>
            <aside className="module-workspace__rail" aria-label={ `${navigationItemLabel(activeTab)} navigation` }>
              <WorkspaceRail
                groups={ railGroups }
                activeItem={ activeItem }
                notifications={ notificationModules }
                onSelect={ openItem }
                collapsed={ workspaceRailCollapsed }
              />
            </aside>

            {/* Sits on the rail divider, so it must live outside the rail —
                the rail clips overflow on the x axis. */}
            <button
              type="button"
              className="module-workspace__rail-toggle"
              onClick={ () => setWorkspaceRailCollapsed((current) => !current) }
              aria-label={ workspaceRailCollapsed ? "Expand section navigation" : "Collapse section navigation" }
              aria-expanded={ !workspaceRailCollapsed }
            >
              { workspaceRailCollapsed ? <RightOutlined /> : <LeftOutlined /> }
            </button>

            <div className="module-workspace__mobile-nav">
              <Button
                block
                icon={ <MenuOutlined /> }
                onClick={ () => setMobileRailOpen(true) }
                aria-label={ `Open ${navigationItemLabel(activeTab)} navigation` }
              >
                <span>{ activeLabel }</span>
                <RightOutlined />
              </Button>
            </div>
          </>
        ) : null }

        <main
          ref={ contentRef }
          id="module-workspace-content"
          className="module-workspace__content"
          tabIndex="-1"
        >
          { loading ? (
            <div className="module-workspace__loading">
              <Spin />
              <span>Loading workspace…</span>
            </div>
          ) : children }
        </main>
      </div>

      <Drawer
        title={ navigationItemLabel(activeTab) }
        placement="left"
        width={ Math.min(340, window.innerWidth - 32) }
        open={ mobileRailOpen }
        onClose={ () => setMobileRailOpen(false) }
        className="module-workspace__drawer"
      >
        <WorkspaceRail
          groups={ railGroups }
          activeItem={ activeItem }
          notifications={ notificationModules }
          onSelect={ openItem }
        />
      </Drawer>
    </section>
    </WorkspaceTabsContext.Provider>
  );
};

const WorkspaceRail = ({
  groups,
  activeItem,
  notifications,
  onSelect,
  collapsed = false,
}) => (
  <nav className={ `module-workspace-rail${collapsed ? " module-workspace-rail--collapsed" : ""}` }>
    { groups.map((group) => (
      <section key={ group.key } className="module-workspace-rail__group">
        <h2>{ group.label }</h2>
        { group.items.map((item) => {
          const active = item.id === activeItem?.id;
          const count = getNotificationCount(notifications, item);

          return (
            <button
              key={ item.id || item.module_key }
              type="button"
              className={ `module-workspace-rail__item${active ? " module-workspace-rail__item--active" : ""}` }
              onClick={ () => onSelect(item) }
              aria-current={ active ? "page" : undefined }
              title={ collapsed ? navigationItemLabel(item) : undefined }
            >
              <AppstoreOutlined />
              <span>{ navigationItemLabel(item) }</span>
              { count > 0 ? <Badge count={ count } overflowCount={ 99 } /> : null }
            </button>
          );
        }) }
      </section>
    )) }
  </nav>
);

const getNotificationCount = (modules, item) => {
  if (!modules?.length || !item) return 0;

  const keys = [
    item.module_key,
    item.name,
    item.sref,
    item.tag,
    item.tags,
  ].map(normalizeNavKey).filter(Boolean);

  const countMatches = (items) => (items || []).reduce((total, notification) => {
    const notificationKeys = [
      notification.module_key,
      notification.moduleKey,
      notification.nav_key,
      notification.key,
      notification.name,
      notification.code,
      notification.sref,
      notification.identifier,
      notification.tag,
      notification.tags,
    ].map(normalizeNavKey).filter(Boolean);
    const value = Number(
      notification.value
      ?? notification.count
      ?? notification.total
      ?? notification.notification_count
      ?? 0
    );

    return total
      + (notificationKeys.some((key) => keys.includes(key)) && !Number.isNaN(value) ? value : 0)
      + countMatches(notification.sub_modules || []);
  }, 0);

  return countMatches(modules)
    + (item.children || []).reduce(
      (total, child) => total + getNotificationCount(modules, child),
      0
    );
};

export const scrollTabHorizontally = (tab) => {
  const container = tab?.parentElement;
  if (!container) return;

  const tabLeft = tab.offsetLeft;
  const tabRight = tabLeft + tab.offsetWidth;
  const visibleLeft = container.scrollLeft;
  const visibleRight = visibleLeft + container.clientWidth;
  const edgePadding = 12;

  if (tabLeft < visibleLeft) {
    container.scrollTo({ left: Math.max(tabLeft - edgePadding, 0), behavior: "smooth" });
  } else if (tabRight > visibleRight) {
    container.scrollTo({
      left: tabRight - container.clientWidth + edgePadding,
      behavior: "smooth",
    });
  }
};

export default ModuleWorkspace;
